import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Lock, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PricingDialog from "@/components/PricingDialog";

const WeeklyReport = () => {
  const { isPremium } = useAuth();
  const [report, setReport] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState<{ event_count: number; countries: number; generated_at: string } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("weekly-report");
      if (error) throw error;
      setReport(data.report);
      setMeta({ event_count: data.event_count, countries: data.countries, generated_at: data.generated_at });
      setExpanded(true);
    } catch (e: any) {
      console.error("Report error:", e);
    } finally {
      setLoading(false);
    }
  };

  // Blurred preview for free users
  const PREVIEW_TEXT = `# WEEKLY INTELLIGENCE REPORT

## EXECUTIVE SUMMARY
127 conflict events recorded across 14 countries in the past 7 days. Significant escalation observed in the Middle East theater with increased missile activity. Eastern European front remains active with sustained drone and air raid operations.

## KEY DEVELOPMENTS
1. Surge in missile launches targeting critical infrastructure in multiple theaters
2. Unprecedented drone activity detected near strategic maritime chokepoints
3. Military movement patterns suggest force repositioning in contested zones...`;

  return (
    <section className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-4 flex items-center gap-3">
          <FileText className="h-5 w-5 text-primary" />
          <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-foreground">
            Weekly Intelligence Report
          </h2>
          <Badge variant="outline" className="text-[10px] font-mono border-primary/30 text-primary">
            AI-GENERATED
          </Badge>
        </div>

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {!isPremium ? (
            <>
              {/* Blurred preview */}
              <div className="relative">
                <div className="p-5 blur-sm select-none pointer-events-none">
                  <pre className="whitespace-pre-wrap font-mono text-xs text-muted-foreground leading-relaxed">
                    {PREVIEW_TEXT}
                  </pre>
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/60 backdrop-blur-sm">
                  <Lock className="h-8 w-8 text-primary mb-3" />
                  <p className="text-sm font-semibold text-foreground mb-1">Premium Feature</p>
                  <p className="text-xs text-muted-foreground mb-4 text-center max-w-xs">
                    AI-generated weekly intelligence reports from 7 days of OSINT data
                  </p>
                  <Button onClick={() => setPricingOpen(true)} className="gap-1.5">
                    Unlock Weekly Reports
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-5">
              {!report ? (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 text-primary mx-auto mb-3 opacity-50" />
                  <p className="text-sm text-muted-foreground mb-4">
                    Generate a comprehensive intelligence report from the last 7 days of conflict data.
                  </p>
                  <Button onClick={generateReport} disabled={loading} className="gap-1.5">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                    {loading ? "Generating report..." : "Generate Weekly Report"}
                  </Button>
                </div>
              ) : (
                <>
                  {meta && (
                    <div className="flex flex-wrap gap-3 mb-4 text-[10px] font-mono text-muted-foreground">
                      <span>📊 {meta.event_count} events analyzed</span>
                      <span>🌍 {meta.countries} countries</span>
                      <span>📅 {new Date(meta.generated_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className={`relative ${!expanded ? "max-h-48 overflow-hidden" : ""}`}>
                    <pre className="whitespace-pre-wrap font-mono text-xs text-foreground leading-relaxed">
                      {report}
                    </pre>
                    {!expanded && (
                      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-card to-transparent" />
                    )}
                  </div>
                  <div className="mt-3 flex justify-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="gap-1 text-xs">
                      {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      {expanded ? "Collapse" : "Read full report"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={generateReport} disabled={loading} className="gap-1 text-xs">
                      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                      Regenerate
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </section>
  );
};

export default WeeklyReport;
